// lib/koi-pond-v3/behaviors/koiPathFollowing.ts
//
// T8.4 — path following. Each koi wanders the pond on lazy S-curves
// instead of swimming in place. CPU steering (the swim wave itself stays
// on the GPU): per frame we evolve a heading + speed, integrate position
// in the XZ plane, and build an orientation that banks into the turns.
//
// WATCH-ITEMS (owner T8.4 review)
//   - Lazy S-curves: heading is driven by layered sines (gentle, never
//     straight for long), not a straight line.
//   - Bank/roll into turns: orientation rolls about the travel axis
//     proportional to the turn rate, so the koi leans into the curve
//     instead of sliding flat.
//   - Wave/travel coherence: the mesh's +X (head) is aligned to the
//     velocity every frame, so the GPU swim wave (head→tail) always
//     pushes the koi the right way as it turns.
//   - In-bounds + composition: a soft elliptical boundary steers the koi
//     back toward a centre biased slightly toward the moon-reflection
//     side, keeping them on-camera and leading the eye.
//   - Graceful speed: cruise varies gently with rare brief accelerations
//     and never a full stop; the speed feeds uSwimGain (clamped) so a
//     faster koi flexes harder without going frantic.

import { Vector3, Quaternion, Matrix4 } from 'three';
import { leapArc, leapWarp, type Vec3 } from '../interaction/dragonGatePhases';

export interface KoiSwimmerOptions {
  start: Vector3; // initial position
  heading: number; // initial heading (radians, XZ plane from +X toward +Z)
  baseSpeed: number; // cruise speed (world units / sec)
  baseBeat: number; // base uSwimSpeed (tail-beat rate) for this koi
  seed: number; // per-koi phase seed so paths differ
  bounds: { cx: number; cz: number; rx: number; rz: number }; // soft XZ ellipse
  depthCenter: number; // T8.5 preferred depth (world y) — mostly midwater
  excursionSpeedup?: number; // TEMP review aid — multiplies the rare
  // surface/floor excursion frequency so they're observable in a short
  // window. 1 = production cadence (~150s periods).
}

// A neighbour's planar position, for T8.6 mutual-awareness steering.
export interface NeighborXZ {
  x: number;
  z: number;
}

const SEPARATION_R = 0.85; // steer apart inside this radius (avoid passing through)
const GROUP_R = 2.2; // sociability range for occasional loose pairing

export interface KoiSwimState {
  position: Vector3;
  quaternion: Quaternion;
  gain: number; // → uSwimGain (amplitude scale)
  beat: number; // → uSwimSpeed (tail-beat rate)
}

export function makeKoiSwimmer(opts: KoiSwimmerOptions) {
  const pos = opts.start.clone();
  let heading = opts.heading;
  let lastT = -1;
  // T11.6 — surface-break urge: while active (t < urgeUntil) the koi is pulled
  // strongly toward the surface so a scroll gesture produces a visible rise +
  // reflection break. Set via the returned urgeSurface(); decays on its own.
  let urgeUntil = -1;
  // Dragon Gate — full ballistic leap override (set via leap(); -1 = inactive).
  let leapFrom = -1;
  let leapDur = 1.6;
  let leapStartP: Vec3 = { x: 0, y: 0, z: 0 };
  let leapApex: Vec3 = { x: 0, y: 0, z: 0 };
  let leapEnd: Vec3 = { x: 0, y: 0, z: 0 };

  const quaternion = new Quaternion();
  const state: KoiSwimState = { position: pos, quaternion, gain: 1, beat: opts.baseBeat };

  // scratch (avoid per-frame allocation)
  const fwd = new Vector3();
  const up = new Vector3();
  const side = new Vector3();
  const worldUp = new Vector3(0, 1, 0);
  const basis = new Matrix4();
  const qRoll = new Quaternion();

  const wrapPi = (a: number) => Math.atan2(Math.sin(a), Math.cos(a));
  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

  function update(
    t: number,
    neighbors: NeighborXZ[] = [],
    converge: { x: number; z: number; strength: number } | null = null,
    avoid: { x: number; z: number; strength: number } | null = null,
  ): KoiSwimState {
    // Dragon Gate — full ballistic leap override. While leaping, ignore all
    // path-following steering and trace the arc; keep `pos` coherent so normal
    // swim resumes cleanly at the landing point.
    if (leapFrom >= 0) {
      const lt = (t - leapFrom) / leapDur;
      if (lt >= 1) {
        leapFrom = -1;
        pos.set(leapEnd.x, leapEnd.y, leapEnd.z);
      } else {
        // leapWarp holds the bézier param at 0.5 through the apex → the koi
        // hangs near-still at the top (held dragon-gate silhouette).
        const { pos: p, tangent } = leapArc(leapStartP, leapApex, leapEnd, leapWarp(lt < 0 ? 0 : lt));
        pos.set(p.x, p.y, p.z);
        fwd.set(tangent.x, tangent.y, tangent.z).normalize();
        side.crossVectors(fwd, worldUp).normalize();
        up.crossVectors(side, fwd).normalize();
        basis.makeBasis(fwd, up, side);
        quaternion.setFromRotationMatrix(basis);
        state.gain = 1.8;                 // hard flex mid-leap
        state.beat = opts.baseBeat * 1.9;
        lastT = t;                        // keep dt sane on resume
        return state;
      }
    }

    let dt = lastT < 0 ? 0 : t - lastT;
    lastT = t;
    if (dt > 0.05) dt = 0.05; // clamp big gaps (tab blur / first frame)

    const s = opts.seed;
    const cs = converge ? converge.strength : 0; // 0..1 convergence pull

    // Lazy wander — two sine layers give gentle, non-repeating S-curves.
    // Quiet the wander while a convergence is pulling so the koi actually
    // gather rather than meander past the point.
    let omega =
      (1 - 0.7 * cs) * (0.55 * Math.sin(t * 0.23 + s) + 0.3 * Math.sin(t * 0.49 + s * 1.7));

    // Soft boundary: when the koi nears the ellipse edge, add a turn back
    // toward the centre, scaled by how far out it is.
    const ex = (pos.x - opts.bounds.cx) / opts.bounds.rx;
    const ez = (pos.z - opts.bounds.cz) / opts.bounds.rz;
    const out = Math.hypot(ex, ez); // 1.0 ≈ at the boundary
    if (out > 0.8) {
      const toCenter = Math.atan2(opts.bounds.cz - pos.z, opts.bounds.cx - pos.x);
      omega += wrapPi(toCenter - heading) * (out - 0.8) * 5.0;
    }

    // T8.6 — mutual awareness (planar, proximity-based steering; no flock
    // model). Find the nearest neighbour: if it's too close, steer away so
    // the koi don't pass through each other; otherwise an occasional, slow
    // sociability window lets a pair drift loosely together for a while.
    let nearDx = 0;
    let nearDz = 0;
    let nearD = Infinity;
    for (const n of neighbors) {
      const dx = n.x - pos.x;
      const dz = n.z - pos.z;
      const d = Math.hypot(dx, dz);
      if (d < nearD) {
        nearD = d;
        nearDx = dx;
        nearDz = dz;
      }
    }
    if (nearD < Infinity) {
      const bearing = Math.atan2(nearDz, nearDx); // toward the neighbour
      if (nearD < SEPARATION_R) {
        const away = bearing + Math.PI;
        omega += wrapPi(away - heading) * (1 - nearD / SEPARATION_R) * 3.0;
      } else if (nearD < GROUP_R) {
        // sociability: pow(sin) keeps it mostly 0 with occasional windows
        const social = Math.pow(Math.max(0, Math.sin(t * 0.05 + s * 1.5)), 4.0);
        if (social > 0.2) omega += wrapPi(bearing - heading) * social * 0.6;
      }
    }

    // T10.4 — convergence: steer toward the shared point while a gathering
    // is active. Separation (above) keeps them from stacking, so they
    // hover loosely around the point rather than piling on it.
    if (cs > 0.01 && converge) {
      const bearing = Math.atan2(converge.z - pos.z, converge.x - pos.x);
      omega += wrapPi(bearing - heading) * cs * 1.6;
    }

    // T11.1 — cursor avoidance: veer AWAY from the cursor's projected
    // point, scaled by strength (which the scene scales by cursor speed →
    // slow approach = mild veer, fast swoop = sharp startle).
    let startle = 0;
    if (avoid && avoid.strength > 0.01) {
      const bearingAway = Math.atan2(pos.z - avoid.z, pos.x - avoid.x);
      omega += wrapPi(bearingAway - heading) * Math.min(avoid.strength, 1.5) * 2.2;
      startle = Math.min(avoid.strength, 1.5);
    }

    heading += omega * dt;

    // Speed — gentle cruise + rare brief accelerations (sharp pow keeps
    // the pulse sparse), never a full stop.
    const cruise = 0.55 + 0.32 * Math.sin(t * 0.27 + s * 1.9);
    const pulse = Math.pow(Math.max(0, Math.sin(t * 0.11 + s)), 6.0) * 0.7;
    // startle adds a brief dart when fleeing the cursor
    const speedFactor = Math.max(0.14, cruise + pulse + startle * 0.7);
    const speed = opts.baseSpeed * speedFactor;

    pos.x += Math.cos(heading) * speed * dt;
    pos.z += Math.sin(heading) * speed * dt;

    // T8.5 — depth band: mostly the preferred depth with a gentle bob,
    // plus RARE excursions toward the surface (sin^8 → sparse) or the
    // floor. Ease toward the target so depth changes are slow.
    const xs = opts.excursionSpeedup ?? 1;
    const bob = 0.12 * Math.sin(t * 0.19 + s * 1.3);
    const rise = Math.pow(Math.max(0, Math.sin(t * 0.043 * xs + s)), 8.0);
    const dive = Math.pow(Math.max(0, Math.sin(t * 0.037 * xs + s * 2.1)), 8.0);
    let targetY =
      opts.depthCenter + bob + rise * (0.45 - opts.depthCenter) - dive * 0.9;
    // T11.6 — during a surface-break urge, override toward the top of the band
    // and ease faster so the koi reaches the surface within the scroll window.
    const urging = t < urgeUntil;
    if (urging) targetY = Math.max(targetY, 0.45);
    const oldY = pos.y;
    pos.y += (targetY - pos.y) * Math.min(1, dt * (urging ? 1.6 : 0.6));
    pos.y = clamp(pos.y, -1.9, 0.45);
    const vy = dt > 0 ? (pos.y - oldY) / dt : 0;
    // slight nose pitch into the climb/dive so vertical moves don't look flat
    const pitch = clamp(Math.atan2(vy, Math.max(0.12, speed)) * 0.6, -0.3, 0.3);

    // Orientation: local +X → travel direction; right-handed basis
    // (fwd, up, side) so the body stays upright, then roll about the
    // forward axis to bank into the turn.
    fwd
      .set(
        Math.cos(heading) * Math.cos(pitch),
        Math.sin(pitch),
        Math.sin(heading) * Math.cos(pitch),
      )
      .normalize();
    side.crossVectors(fwd, worldUp).normalize(); // = fwd × up
    up.crossVectors(side, fwd).normalize(); // keeps (fwd, up, side) right-handed
    basis.makeBasis(fwd, up, side);
    quaternion.setFromRotationMatrix(basis);
    // Bank INTO the turn (~23° max): a +roll about fwd tilts the top
    // toward local +side, which is the inside of the turn when omega>0.
    const roll = clamp(omega * 0.32, -0.4, 0.4);
    qRoll.setFromAxisAngle(fwd, roll);
    quaternion.premultiply(qRoll);

    // Outputs for the swim shader. gain scales amplitude (graceful, never
    // frantic); beat nudges the tail-beat rate up a touch when faster.
    const norm = clamp(speedFactor, 0, 1.4);
    state.gain = 0.7 + 0.5 * norm;
    state.beat = opts.baseBeat * (0.85 + 0.4 * norm);
    return state;
  }

  // T11.6 — pull this koi toward the surface for `durationSec` from time `t`.
  function urgeSurface(t: number, durationSec = 3) {
    urgeUntil = t + durationSec;
  }

  // Dragon Gate — start a ballistic leap from the current position through
  // `apex` to `end` over `duration` seconds. update() fully overrides
  // path-following while it runs.
  function leap(t: number, arc: { apex: Vec3; end: Vec3; duration?: number }) {
    leapFrom = t;
    leapDur = arc.duration ?? 1.6;
    leapStartP = { x: pos.x, y: pos.y, z: pos.z };
    leapApex = arc.apex;
    leapEnd = arc.end;
  }
  function isLeaping(t: number) {
    return leapFrom >= 0 && t - leapFrom < leapDur;
  }

  return { state, update, urgeSurface, leap, isLeaping };
}
